import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Wand2,
  Upload,
  Link as LinkIcon,
  FileText,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Download,
  Sparkles,
} from "lucide-react";
import { PageHeader, Field, Empty } from "@/components/UI";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { formatDate } from "@/lib/format";

const STEPS = ["Source", "Extract", "Review", "Compile"];

export default function BidWizard() {
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState("url"); // url | pdf | text
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [hint, setHint] = useState("Uganda ICT tender");
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [savedBidId, setSavedBidId] = useState(null);
  const [attaching, setAttaching] = useState(false);
  const [vault, setVault] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [compiling, setCompiling] = useState(false);
  const { toast } = useToast();
  const nav = useNavigate();
  const [params] = useSearchParams();

  useEffect(() => {
    api.listDocuments().then(setVault).catch(() => {});
  }, []);

  const doExtract = useCallback(async () => {
    setExtracting(true);
    try {
      let res;
      if (mode === "url") {
        if (!url) throw new Error("Enter a URL");
        res = await api.extractBidFromUrl(url, hint);
      } else if (mode === "pdf") {
        if (!file) throw new Error("Choose a PDF");
        res = await api.extractBidFromPdf(file, hint);
      } else {
        if (!text) throw new Error("Paste RFP text");
        res = await api.extractBidFromText(text, hint);
      }
      setExtracted(res);
      setStep(2);
    } catch (e) {
      toast(e?.response?.data?.detail || e.message || "Extraction failed", "error");
    } finally {
      setExtracting(false);
    }
  }, [mode, url, file, text, hint, toast]);

  // Prefill from Feed → "Analyse with AI" quick action
  useEffect(() => {
    const seedUrl = params.get("url");
    const seedText = params.get("text");
    const seedHint = params.get("hint");
    if (seedUrl) { setMode("url"); setUrl(seedUrl); }
    else if (seedText) { setMode("text"); setText(seedText); }
    if (seedHint) setHint(seedHint);
    if (params.get("auto") === "1" && (seedUrl || seedText)) {
      // Kick off extraction on the next tick so state is set
      setTimeout(() => doExtract(), 100);
    }
  }, [params, doExtract]);

  const saveAsBid = async () => {
    setAttaching(true);
    try {
      // Create bid via generic /bids using extracted content
      const payload = {
        title: extracted.title || "New bid",
        client_name: extracted.client || "",
        status: "Draft",
        service_type: "ICT",
        bid_amount: 0,
        currency: extracted.currency || "UGX",
        deadline: extracted.deadline_iso || "",
        scope_of_work: extracted.scope_summary || "",
        ref_number: extracted.reference_number || "",
        source: "wizard",
        notes: [
          `Bid security: ${extracted.bid_security_required ? "YES" : "NO"} ${extracted.bid_security_amount}`,
          `Doc purchase: ${extracted.doc_purchase_required ? "YES" : "NO"} ${extracted.doc_purchase_fee}`,
          `Contact: ${extracted.contact_person || "-"} (${extracted.contact_email || "-"})`,
          `Submission: ${extracted.submission_method || "-"}`,
          "",
          "Key requirements:",
          ...(extracted.key_requirements || []).map((k) => "- " + k),
        ].join("\n"),
        attachments: selectedDocs,
      };
      const bid = await api.createBid(payload);
      setSavedBidId(bid.id);
      toast(`Bid saved (${bid.id.slice(0, 8)}…)`, "success");
      setStep(3);
    } catch (e) {
      toast(e?.response?.data?.detail || "Failed to save bid", "error");
    } finally {
      setAttaching(false);
    }
  };

  const compilePdf = async () => {
    if (!savedBidId) return;
    setCompiling(true);
    try {
      const res = await api.compileBidPdf(savedBidId);
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      toast("Compiled PDF ready — opening in a new tab", "success");
    } catch (e) {
      toast(e?.response?.data?.detail || "PDF compilation failed", "error");
    } finally {
      setCompiling(false);
    }
  };

  return (
    <div data-testid="page-bid-wizard">
      <PageHeader
        icon={Wand2}
        title="Bid Wizard"
        subtitle="AI-powered RFP extraction · PPDA-ordered compliance pack · single compiled PDF"
      />

      {/* Steps ribbon */}
      <div className="card card-padded mb-5">
        <div className="flex items-center gap-2 overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 shrink-0">
              <div
                data-testid={`step-${i}`}
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  i < step
                    ? "bg-emerald-100 text-emerald-700"
                    : i === step
                    ? "bg-[#0e6939] text-white"
                    : "bg-slate-100 text-slate-500"
                }`}
              >
                {i < step ? <CheckCircle2 size={13} /> : i + 1}
              </div>
              <span className={`text-sm font-medium ${i === step ? "text-[#0e6939]" : "muted"}`}>{s}</span>
              {i < STEPS.length - 1 && <ArrowRight size={12} className="text-slate-300" />}
            </div>
          ))}
        </div>
      </div>

      {step === 0 && (
        <div className="card card-padded space-y-4" data-testid="step-source">
          <div className="text-sm font-semibold">Choose the source</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {[
              ["url", LinkIcon, "URL"],
              ["pdf", Upload, "PDF upload"],
              ["text", FileText, "Paste text"],
            ].map(([k, Icon, label]) => (
              <button
                key={k}
                data-testid={`mode-${k}`}
                onClick={() => setMode(k)}
                className={`p-4 rounded-xl border-2 text-left transition ${
                  mode === k
                    ? "border-[#0e6939] bg-[var(--green-50)]"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <Icon size={18} style={{ color: "var(--green-700)" }} />
                <div className="mt-2 font-semibold text-sm">{label}</div>
              </button>
            ))}
          </div>

          {mode === "url" && (
            <Field label="Bid / RFP URL">
              <input
                data-testid="wiz-url"
                className="input"
                placeholder="https://egpuganda.go.ug/… or a UNGM notice URL"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
            </Field>
          )}
          {mode === "pdf" && (
            <Field label="RFP PDF">
              <input
                data-testid="wiz-pdf"
                type="file"
                accept="application/pdf"
                className="input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Field>
          )}
          {mode === "text" && (
            <Field label="RFP / bid notice text">
              <textarea
                data-testid="wiz-text"
                rows={8}
                className="textarea"
                placeholder="Paste the full text of the RFP…"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </Field>
          )}

          <Field label="Hint (helps the AI)">
            <input
              className="input"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              placeholder="e.g. 'Uganda ICT tender', 'UNGM notice', 'PPDA framework'"
            />
          </Field>

          <div className="flex justify-end">
            <button
              data-testid="wiz-extract"
              onClick={() => { setStep(1); doExtract(); }}
              disabled={extracting || (mode === "url" && !url) || (mode === "pdf" && !file) || (mode === "text" && !text)}
              className="btn btn-primary"
            >
              {extracting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Extract with Gemini
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="card card-padded text-center py-14" data-testid="step-extract">
          <Loader2 size={30} className="animate-spin mx-auto mb-4" style={{ color: "var(--green-700)" }} />
          <div className="font-bold text-base">Gemini is reading the RFP…</div>
          <div className="text-sm muted mt-2">This usually takes 8–15 seconds.</div>
        </div>
      )}

      {step === 2 && extracted && (
        <div className="space-y-5" data-testid="step-review">
          <div className="card card-padded space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles size={14} style={{ color: "var(--green-700)" }} />
              <span className="font-bold text-sm">Extracted fields</span>
              <span className="text-xs muted ml-auto">Edit anything before saving</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Title">
                <input className="input" data-testid="rev-title" value={extracted.title} onChange={(e) => setExtracted({ ...extracted, title: e.target.value })} />
              </Field>
              <Field label="Client">
                <input className="input" value={extracted.client} onChange={(e) => setExtracted({ ...extracted, client: e.target.value })} />
              </Field>
              <Field label="Reference #">
                <input className="input" value={extracted.reference_number} onChange={(e) => setExtracted({ ...extracted, reference_number: e.target.value })} />
              </Field>
              <Field label="Deadline">
                <input type="date" className="input" value={extracted.deadline_iso} onChange={(e) => setExtracted({ ...extracted, deadline_iso: e.target.value })} />
              </Field>
              <Field label="Currency">
                <input className="input" value={extracted.currency} onChange={(e) => setExtracted({ ...extracted, currency: e.target.value })} />
              </Field>
              <Field label="Submission method">
                <input className="input" value={extracted.submission_method} onChange={(e) => setExtracted({ ...extracted, submission_method: e.target.value })} />
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div
                className="rounded-lg p-3 border"
                style={{
                  background: extracted.bid_security_required ? "#fef2f2" : "var(--green-50)",
                  borderColor: extracted.bid_security_required ? "#fecaca" : "#d6e8de",
                }}
              >
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    data-testid="rev-bidsec"
                    checked={!!extracted.bid_security_required}
                    onChange={(e) => setExtracted({ ...extracted, bid_security_required: e.target.checked })}
                  />
                  <AlertCircle size={13} className={extracted.bid_security_required ? "text-red-700" : "text-emerald-700"} />
                  Bid security required
                </label>
                <input
                  className="input mt-2"
                  placeholder="e.g. UGX 5,000,000"
                  value={extracted.bid_security_amount}
                  onChange={(e) => setExtracted({ ...extracted, bid_security_amount: e.target.value })}
                />
              </div>
              <div
                className="rounded-lg p-3 border"
                style={{
                  background: extracted.doc_purchase_required ? "#fef2f2" : "var(--green-50)",
                  borderColor: extracted.doc_purchase_required ? "#fecaca" : "#d6e8de",
                }}
              >
                <label className="flex items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    data-testid="rev-docpurch"
                    checked={!!extracted.doc_purchase_required}
                    onChange={(e) => setExtracted({ ...extracted, doc_purchase_required: e.target.checked })}
                  />
                  <AlertCircle size={13} className={extracted.doc_purchase_required ? "text-red-700" : "text-emerald-700"} />
                  Bid document purchase required
                </label>
                <input
                  className="input mt-2"
                  placeholder="e.g. UGX 100,000"
                  value={extracted.doc_purchase_fee}
                  onChange={(e) => setExtracted({ ...extracted, doc_purchase_fee: e.target.value })}
                />
              </div>
            </div>

            <Field label="Scope summary">
              <textarea rows={4} className="textarea" value={extracted.scope_summary} onChange={(e) => setExtracted({ ...extracted, scope_summary: e.target.value })} />
            </Field>

            <Field label="Key requirements">
              <div className="space-y-1">
                {(extracted.key_requirements || []).map((k, i) => (
                  // Requirements are strings without stable IDs; composite
                  // key using content prevents reconciliation on delete/reorder.
                  <div key={`req-${i}-${(k || "").slice(0, 12)}`} className="flex items-center gap-2">
                    <span className="text-xs muted w-5">{i + 1}.</span>
                    <input
                      className="input"
                      value={k}
                      onChange={(e) => {
                        const next = [...extracted.key_requirements];
                        next[i] = e.target.value;
                        setExtracted({ ...extracted, key_requirements: next });
                      }}
                    />
                  </div>
                ))}
              </div>
            </Field>
          </div>

          <div className="card card-padded">
            <div className="font-bold text-sm mb-2">Attach compliance certificates (PPDA order)</div>
            <div className="text-xs muted mb-3">Select docs from your Vault. They will be appended to the compiled bid PDF in the correct order.</div>
            {vault.length === 0 ? (
              <Empty icon={FileText} title="Vault is empty" description="Upload your Trading Licence, Tax Clearance, NSSF, NITA-U etc. first." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                {vault.map((d) => (
                  <label
                    key={d.id}
                    className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer border transition ${
                      selectedDocs.includes(d.id)
                        ? "bg-[var(--green-50)] border-[#0e6939]"
                        : "border-slate-100 hover:border-slate-200"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      data-testid={`attach-${d.id}`}
                      checked={selectedDocs.includes(d.id)}
                      onChange={(e) => {
                        setSelectedDocs(
                          e.target.checked
                            ? [...selectedDocs, d.id]
                            : selectedDocs.filter((x) => x !== d.id),
                        );
                      }}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{d.file_name}</div>
                      <div className="text-xs muted">
                        {d.category || d.folder}
                        {d.expiry_date ? ` · expires ${d.expiry_date}` : ""}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <button onClick={() => setStep(0)} className="btn btn-ghost">Back</button>
            <button
              data-testid="wiz-save-bid"
              onClick={saveAsBid}
              disabled={attaching || !extracted.title}
              className="btn btn-primary"
            >
              {attaching ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              Save as bid & continue
            </button>
          </div>
        </div>
      )}

      {step === 3 && savedBidId && (
        <div className="card card-padded text-center py-10" data-testid="step-compile">
          <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4"
               style={{ background: "var(--green-50)", color: "var(--green-700)" }}>
            <CheckCircle2 size={22} />
          </div>
          <div className="font-bold text-lg mb-1">Bid saved ✓</div>
          <div className="text-sm muted mb-6">
            Ready to compile the single-PDF submission pack. Cover page + your selected certificates in PPDA order.
          </div>
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => nav("/bids")} className="btn btn-ghost">Open in Bids</button>
            <button
              data-testid="wiz-compile"
              onClick={compilePdf}
              disabled={compiling}
              className="btn btn-primary"
            >
              {compiling ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Compile submission PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
